import React, { memo } from "react";
import classes from "./Fields.scss";
import classNames from "classnames";
import Unveil from "UIComponents/Unveil";
import { colors, fontStyle } from "Constants";
import { getPlannerFieldOptions } from "UnitPlans/modules/UnitPlanModule";
import FieldComponent from "UnitPlans/components/IBcomponents/FieldComponent";
import LabelComponent from "UnitPlans/components/LabelComponent";
import {
  getPropertyValue,
  getEmptyFieldValueStatus,
  getPlannerFieldOptions as getPlannerFieldV2Options,
} from "Fields/FieldUtils";
import { getUnitPlanDetailsValueFromFieldKey } from "IBPlanner/modules/IBPlannerModule";
import _ from "lodash";
import { connect } from "react-redux";

const inputStyle = {
  labelStyle: {
    ...fontStyle.demiBold,
    fontSize: "1.4rem",
    lineHeight: "20px",
    color: colors.neutral200,
  },
  textStyle: {
    ...fontStyle.medium,
    fontSize: "1.4rem",
    lineHeight: "20px",
    color: colors.neutral200,
  },
};
const nodeLabelStyle = [
  {
    depth: 0,
    style: {
      ...inputStyle.labelStyle,
    },
  },
  {
    depth: 1,
    style: {
      ...inputStyle.labelStyle,
    },
  },
  {
    depth: 2,
    style: {
      ...inputStyle.textStyle,
    },
  },
  {
    depth: 3,
    style: {
      ...inputStyle.textStyle,
    },
  },
];

// function to return custom style according to the viewType component from the template
const getFieldCustomStyles = ({ viewType }) => {
  switch (viewType) {
    case "Nestedview":
    case "ScopeAndSequence":
    case "NESTED_CHECKLIST":
      return {
        nodeLabelStyle,
      };
    case "RichText":
      return {
        applyDefaultClass: false,
        customTextStyle: inputStyle.textStyle,
      };
    case "ListView":
    case "InputNumberList":
      return {
        contentStyle: inputStyle.textStyle,
      };
    case "TextView":
      return {
        textViewcustomTextStyle: inputStyle.textStyle,
        isApplyCustomTextStyle: true,
      };
    case "GroupedConceptsView":
      return {
        headerLabelStyle: inputStyle.labelStyle,
        conceptLabelStyle: inputStyle.textStyle,
      };
    case "MultiSelectWithImage":
    case "IBMultiSelectQuestion":
      return {
        customTextStyle: inputStyle.textStyle,
        customLabelStyle: inputStyle.labelStyle,
      };
    case "IBRelatedConcepts":
      return {
        headerLabelStyle: inputStyle.labelStyle,
        listHeaderStyle: inputStyle.labelStyle,
        listItemsStyle: inputStyle.textStyle,
      };
    case "CARD_Ported":
    case "CardView":
      return {
        cardTextStyle: inputStyle.textStyle,
        cardStyle: { marginBottom: "8px" },
      };
    case "DateDurationView":
      return {
        dateStyle: inputStyle.textStyle,
        labelStyle: inputStyle.labelStyle,
      };
    default:
      return {
        textStyle: inputStyle.textStyle,
        customTextStyle: inputStyle.textStyle,
      };
  }
};

// Function to get options with subject filtering for V2 fields - similar to UnitPlanContainer
const getOptionsWithSubjectFilteringV2 = params => {
  const {
    fieldKey,
    fieldObj,
    unitPlanFields,
    currentCurriculumProgramType,
    grades,
    plannerElementNodeSets,
  } = params;

  // Get basic options from field configuration
  const basicOptions = getPropertyValue({
    propertyPath: "options",
    fieldObj,
    defaultValue: JSON.stringify({}),
  });

  // Parse options if they're a JSON string
  let options = basicOptions;
  try {
    if (typeof basicOptions === "string") {
      options = JSON.parse(basicOptions);
    }
  } catch (e) {
    console.warn("Failed to parse field options for field:", fieldKey, e);
    options = {};
  }

  try {
    // Use the same utility function as UnitPlanContainer for V2 fields
    const enhancedOptions = getPlannerFieldV2Options({
      grades,
      fieldObj,
      fieldKey,
      unitPlanFields,
      plannerElementNodeSets,
    });

    if (enhancedOptions && Object.keys(enhancedOptions).length > 0) {
      options = enhancedOptions;
    }
  } catch (e) {
    console.warn("Failed to get enhanced options for field:", fieldKey, e);
  }

  // Apply subject filtering similar to UnitPlanContainer's getOptionsWithSubjectFiltering
  if (
    _.isEqual(currentCurriculumProgramType, "PYP") &&
    _.includes(fieldKey, "subjects")
  ) {
    const subjectIds = _.get(
      _.find(unitPlanFields, { uid: "subjects" }),
      "value",
      []
    );

    if (_.isArray(options)) {
      const filteredSubjects = _.filter(
        options,
        option => !option.isArchived || _.includes(subjectIds, option.value)
      );
      return filteredSubjects;
    }
  }

  // Check if field is theme and has descriptor with semicolons
  if (_.includes(fieldKey, "theme") && _.isArray(options)) {
    return _.map(options, option => {
      const subText = _.get(option, "subText", "");
      if (_.includes(subText, ";")) {
        return {
          ...option,
          subText: null,
        };
      }
      return option;
    });
  }

  return options;
};

const Fields = memo(function Fields(props) {
  const {
    t,
    fields,
    field_list,
    fieldListV2,
    unitPlanFields,
    organizationId,
    unitPlanDetails,
    selectedAcademicYear,
    currentCurriculumProgramType,
    isUnitFlowRevamp,
    mode = "view", // Default to view mode if not specified
    grades,
    plannerElementNodeSets,
  } = props;

  return _.map(fields, fieldKey => {
    // Determine if we're using V2 or V1 field system based on fieldListV2
    return _.has(fieldListV2, fieldKey) ? (
      <WrappedFieldComponentV2
        key={fieldKey}
        unitPlanFields={unitPlanFields}
        fieldKey={fieldKey}
        fieldListV2={fieldListV2}
        unitPlanDetails={unitPlanDetails}
        isUnitFlowRevamp={isUnitFlowRevamp}
        mode={mode}
        currentCurriculumProgramType={currentCurriculumProgramType}
        grades={grades}
        plannerElementNodeSets={plannerElementNodeSets}
      />
    ) : (
      <WrappedFieldComponent
        key={fieldKey}
        unitPlanFields={unitPlanFields}
        fieldKey={fieldKey}
        t={t}
        field_list={field_list}
        organizationId={organizationId}
        selectedAcademicYear={selectedAcademicYear}
        unitPlanDetails={unitPlanDetails}
        isUnitFlowRevamp={isUnitFlowRevamp}
        mode={mode}
        grades={grades}
        plannerElementNodeSets={plannerElementNodeSets}
        //only added curriculumProgramType to V1 since we only require this for
        //subjects and subjectLevel and they are only present in V1 currently
        currentCurriculumProgramType={currentCurriculumProgramType}
      />
    );
  });
});

const WrappedFieldComponent = props => {
  const {
    unitPlanFields,
    fieldKey,
    t,
    field_list,
    organizationId,
    selectedAcademicYear,
    unitPlanDetails,
    currentCurriculumProgramType,
    isUnitFlowRevamp,
    mode = "view", // Default to view mode
    grades,
    plannerElementNodeSets,
  } = props;

  const field = field_list[fieldKey] ?? {};
  const showInUnitSummary = _.get(field, "config.showInUnitSummary", true);
  //skipping fields that have the below flag as false
  if (!showInUnitSummary) return null;
  let { viewType } = field;
  const { type, config: { viewLabel, label, styles, options } = {} } = field;

  // Get the fieldTypeConfig from the field configuration
  const fieldTypeConfig = _.get(field, "config.fieldTypeConfig", {});

  //clean props
  const fieldLabel = viewLabel || label;

  // In view mode, prefer viewType if available, otherwise use type
  viewType = viewType ?? type;

  // Providing Styling
  inputStyle.templateStyle = {
    ..._.get(styles, "style", {}),
    ..._.get(styles, "viewStyle", {}),
  };

  // Get field data object directly as in UnitPlanContainer.js
  const fieldData = _.find(unitPlanFields, { uid: fieldKey }) || {};
  const resolvedValue = _.get(fieldData, "resolvedMinimalTree", {});

  const subjectGroup = _.first(
    _.get(
      _.find(unitPlanFields, { uid: "subjectGroup" }),
      "resolvedMinimalTree.subjectGroups",
      []
    )
  );

  // Initialize values
  let value = null;
  let fileType = "IMAGE";
  let image = null;

  // Get configuration from field
  const isMappedWithDetailsFields = _.get(
    field,
    "config.isMappedWithDetailsFields",
    false
  );
  const mappedDetailsFields = _.get(field, "config.mappedDetailsFields", []);
  const subField = _.get(field, "subField", {});
  let subFieldValue = {};

  // Match exactly how UnitPlanContainer.js handles different field types
  if (isMappedWithDetailsFields) {
    value = getUnitPlanDetailsValueFromFieldKey({
      unitPlanDetails: unitPlanDetails,
      mappedDetailsFields,
      key: fieldKey,
    });
  } else if (
    fieldKey === "subjects" &&
    _.isEqual(currentCurriculumProgramType, "IB_DP")
  ) {
    // Special handling for subjects in DP curriculum (need to combine with subject level)
    const subjectValue = _.get(fieldData, "value", "");

    const subjectLevelFieldData =
      _.find(unitPlanFields, { uid: "subjectLevel" }) || {};
    const subjectLevelValue = _.get(subjectLevelFieldData, "value", "");

    value = {
      subjectValue,
      subjectLevelValue,
      length: 1,
    };
  } else if (fieldKey === "image") {
    const attachment = _.get(resolvedValue, "attachment");

    if (attachment) {
      value = _.get(attachment, "url", "");
      fileType = _.get(attachment, "type", "image");
      image = _.get(attachment, "thumbUrl", "") || value;
    }
  } else if (!_.isEmpty(subField)) {
    value = _.get(fieldData, "value", "");
    const questionFieldUid = _.get(subField, "uid", "");
    subFieldValue = _.find(unitPlanFields, {
      uid: questionFieldUid,
    })?.value;
  } else if (_.isEqual(fieldKey, "dateDuration")) {
    const { startDate, endDate } = unitPlanDetails;
    value = { startDate, endDate, length: 1 };
  } else {
    // For all other fields, use direct field data value
    value = _.get(fieldData, "value", "");
  }

  // Get field custom styles based on the view type
  const customStyles = getFieldCustomStyles({ viewType });

  // Check if this is a rich text field to handle appropriately
  const isRichText = viewType === "RichText";
  const emptyText = isRichText
    ? "not_defined"
    : _.get(field, "config.emptyText") || "-";

  //Displaying Label & value pairs using FieldComponent
  // Use direct check instead of isEmpty for consistency with UnitPlanContainer.js
  if (value !== null && value !== undefined && value !== "") {
    const collapsible = _.get(field, "config.collapsible", true);

    const fieldContainerClasses = classNames({
      [classes.fieldContainer]: true,
      [classes.fieldContainerRevamp]: isUnitFlowRevamp,
    });

    // Get the proper options for field rendering
    const fieldOptions = getPlannerFieldOptions({
      fieldKey: fieldKey,
      defaultOptions: options,
      field_list,
      unitPlanFields,
      organizationId,
      t,
      selectedAcademicYear,
      grades,
      readOnly: true,
      mode,
      plannerElementNodeSets,
    });

    return (
      <div className={fieldContainerClasses}>
        <Unveil
          maxHeight={collapsible ? 240 : "auto"}
          showCollapse={collapsible}
        >
          <div style={inputStyle.templateStyle}>
            <FieldComponent
              isSinglePageAndNewUnitView={true}
              defaultOptions={fieldOptions}
              type={viewType} // Using the resolved viewType (or fallback to type) for view mode
              mode={mode}
              labelComponent={
                <LabelComponent
                  isSinglePageAndNewUnitView={true}
                  label={fieldLabel}
                  labelStyle={{
                    ...inputStyle.labelStyle,
                  }}
                />
              }
              value={value}
              fieldKey={fieldKey}
              fieldObj={fieldData}
              subjectGroup={subjectGroup}
              nodes={resolvedValue.nodes}
              resolvedValue={resolvedValue}
              isApplyTextViewcustomTextStyle={true}
              isListView={field?.config?.isListView}
              filterEmptyValues={field?.config?.filterEmptyValues}
              emptyText={emptyText}
              fileType={fileType}
              image={image}
              subFieldValue={subFieldValue}
              fieldId={fieldData.id || ""}
              currentFieldId={fieldData.id}
              isUnitPlan={true}
              fieldTypeConfig={fieldTypeConfig}
              {...customStyles}
            />
          </div>
        </Unveil>
      </div>
    );
  } else {
    return null;
  }
};

const WrappedFieldComponentV2 = props => {
  const {
    unitPlanFields,
    fieldKey,
    fieldListV2,
    unitPlanDetails,
    isUnitFlowRevamp,
    mode = "view", // Default to view mode if not specified
    currentCurriculumProgramType,
    grades,
    plannerElementNodeSets,
  } = props;

  const fieldObj = fieldListV2[fieldKey];
  const uiType = getPropertyValue({ propertyPath: "uiType", fieldObj });
  const label = getPropertyValue({ propertyPath: "label", fieldObj });
  // Get field-specific custom styling for the view mode
  const customStyles = getFieldCustomStyles({ viewType: uiType });

  // Get template styling from field configuration
  const templateStyle = getPropertyValue({
    fieldObj,
    propertyPath: "templateStyle",
    defaultValue: {},
  });

  // Apply template styling to input style
  inputStyle.templateStyle = {
    ...templateStyle,
    ...getPropertyValue({
      fieldObj,
      propertyPath: "viewStyle",
      defaultValue: {},
    }),
  };

  // Get fieldTypeConfig from field configuration
  const fieldTypeConfig = getPropertyValue({
    fieldObj,
    propertyPath: "fieldTypeConfig",
    defaultValue: {},
  });

  // Extract the field info just like in UnitPlanContainer.js
  const fieldData = _.find(unitPlanFields, { uid: fieldKey }) || {};
  const resolvedValue = _.get(fieldData, "resolvedMinimalTree", {});

  // Initialize variables for value handling
  let value = null;
  let fileType = "IMAGE";
  let image = null;
  let subFieldValue = {};

  if (
    _.get(fieldObj, "uiType.value") === "IBReflection" ||
    _.get(fieldObj, "uiType.value") === "IBReflection_Ported"
  ) {
    return null;
  }

  // Handle fields based on their type as in UnitPlanContainer
  const isMappedWithDetailsFields = getPropertyValue({
    fieldObj,
    propertyPath: "isMappedWithDetailsFields",
    defaultValue: false,
  });

  const mappedDetailsFields = getPropertyValue({
    fieldObj,
    propertyPath: "mappedDetailsFields",
    defaultValue: [],
  });

  // Follow exact same value handling logic as UnitPlanContainer.js
  if (isMappedWithDetailsFields) {
    value = getUnitPlanDetailsValueFromFieldKey({
      unitPlanDetails: unitPlanDetails,
      mappedDetailsFields,
      key: fieldKey,
    });
  } else if (fieldKey === "image") {
    const attachment = _.get(resolvedValue, "attachment");

    if (attachment) {
      value = _.get(attachment, "url", "");
      fileType = _.get(attachment, "type", "image");
      image = _.get(attachment, "thumbUrl", "") || value;
    }
  } else if (_.isEqual(fieldKey, "dateDuration")) {
    const { startDate, endDate } = unitPlanDetails;
    value = { startDate, endDate, length: 1 };
  } else {
    // For all other fields, use direct field data value
    value = _.get(fieldData, "value", "");
  }

  // Handle subfields if any
  const hasSubField = !!getPropertyValue({
    fieldObj,
    propertyPath: "subField",
    defaultValue: null,
  });

  if (hasSubField) {
    const questionFieldUid = getPropertyValue({
      fieldObj,
      propertyPath: "subField.uid",
      defaultValue: "",
    });

    subFieldValue = _.find(unitPlanFields, {
      uid: questionFieldUid,
    })?.value;
  }

  const isValueEmpty = getEmptyFieldValueStatus({
    fieldObj,
    fieldKey,
    data: unitPlanDetails,
  });
  // Skip rendering if value is empty
  if (
    (isValueEmpty && mode === "view") ||
    (value === null && value === undefined && value === "")
  ) {
    return null;
  }

  // Set the container classes with proper styling based on the view type
  const fieldContainerClasses = classNames({
    [classes.fieldContainer]: true,
    [classes.fieldContainerRevamp]: isUnitFlowRevamp,
  });

  // Get the field-specific empty text or use default
  const fieldTypeConfigEmptyText = getPropertyValue({
    fieldObj,
    propertyPath: "fieldTypeConfigEmptyText",
  });

  // Check if this is a rich text field to handle appropriately
  const isRichText = uiType === "RICH_TEXT";
  const emptyText = isRichText
    ? "not_defined"
    : fieldTypeConfigEmptyText || "-";

  // Setup collapsible behavior based on field configuration
  const collapsible = getPropertyValue({
    fieldObj,
    propertyPath: "collapsible",
    defaultValue: true,
  });

  const plannerElementNodeSet = _.find(plannerElementNodeSets, nodeSet => {
    const plannerElementSetId = _.get(nodeSet, "plannerElementSet.id");

    return (
      plannerElementSetId ===
      _.get(fieldListV2[fieldKey], "plannerElementSetId.value")
    );
  });

  return (
    <div className={fieldContainerClasses}>
      <Unveil maxHeight={collapsible ? 240 : "auto"} showCollapse={collapsible}>
        <div style={inputStyle.templateStyle}>
          <FieldComponent
            isSinglePageAndNewUnitView={true}
            value={value}
            fieldKey={fieldKey}
            mode={mode}
            type={`${uiType}_Ported`}
            emptyText={emptyText}
            labelComponent={
              <LabelComponent
                isSinglePageAndNewUnitView={true}
                label={label}
                labelStyle={{
                  ...inputStyle.labelStyle,
                }}
              />
            }
            defaultOptions={getOptionsWithSubjectFilteringV2({
              fieldKey,
              fieldObj,
              unitPlanFields,
              currentCurriculumProgramType,
              grades,
              plannerElementNodeSets,
            })}
            fieldObj={fieldData}
            fieldId={fieldData.id || ""}
            currentFieldId={fieldData.id}
            resolvedValue={resolvedValue}
            isApplyTextViewcustomTextStyle={true}
            fileType={fileType}
            image={image}
            subFieldValue={subFieldValue}
            isUnitPlan={true}
            fieldTypeConfig={fieldTypeConfig}
            plannerElementNodeSet={plannerElementNodeSet}
            {...customStyles}
          />
        </div>
      </Unveil>
    </div>
  );
};

const mapStateToProps = state => {
  const currentCurriculumProgramType = _.get(
    state,
    "platform.currentCurriculumProgram.type",
    ""
  );
  const organizationId = _.get(state, "login.userInfo.org_id", "");
  return {
    currentCurriculumProgramType,
    organizationId,
  };
};

// Set default props for the Fields component
Fields.defaultProps = {
  mode: "view", // Default mode is view
  isUnitFlowRevamp: false,
};

export default connect(mapStateToProps)(Fields);
